import { Grid2Props as GridProps, Unstable_Grid2 as Grid } from "@mui/material";
import { PropsWithChildren } from "react";

interface SenecNestedGridProps extends GridProps {
    /*
     * The variant 'doubleGap' is used to structure content with a rowSpacing of 48px for md and 32px for xs screensize.
     * The variant 'singleGap' is used to structure content with a rowSpacing of 24px for md and 16px for xs screensize.
     * @default "doubleGap"
     */
    variant?: "doubleGap" | "singleGap";
    /**
     * When enabled, adds two more grid column sizes for the largest breakpoints to handle better display of components on very large screens.
     */
    enableLargeColumns?: boolean;
}

/**
 * SenecNestedGrid used to structure content with a spacing of 24px for md and 16px for xs screensize.
 *
 * - The "disableEqualOverflow" Flag is set true, so the content is oriented in the lower right corner and is not centered.
 * - Based on MUI Grid v2: https://mui.com/material-ui/react-grid2/
 * - MUI Spacing based on 8px scaling factor: https://mui.com/material-ui/customization/spacing/
 */
export const SenecNestedGrid = (props: PropsWithChildren<SenecNestedGridProps>) => {
    const { variant = "doubleGap", enableLargeColumns = false, ...remainingProps } = props;

    const getRowSpacing = () => {
        switch (variant) {
            case "doubleGap":
                return { xs: 4, md: 6 };
            case "singleGap":
                return { xs: 2, md: 3 };
        }
    };

    /**
     * Different column number to adjust the column content to the grid
     * e.g. at breakpoint "3xl" a card still uses 2 columns, but takes less space in the grid (with grid column size of 18)
     * so a total of 9 cards can fit instead of 6 (with grid column size of 12)
     */
    const columns = enableLargeColumns ? { "2xl": 15, "3xl": 12, "4xl": 16, "5xl": 20 } : {};

    return (
        <Grid
            container
            disableEqualOverflow
            columns={{ xs: 6, sm: 8, md: 12, ...columns }}
            columnSpacing={{ xs: 2, md: 3 }}
            rowSpacing={getRowSpacing()}
            p={0}
            {...remainingProps}
        >
            {props.children}
        </Grid>
    );
};
